// src/components/common/TurnstileWidget.web.js

import { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';
import { View } from 'react-native';
import env from '../../utils/environment.js';
import logger from '../../utils/logger';

const TURNSTILE_SITE_KEY = env.turnstileSiteKey;
const SCRIPT_ID = 'cloudflare-turnstile-script';

const TurnstileWidget = forwardRef(({ onTokenReceived, onError, invisible }, ref) => {
  const turnstileRef = useRef(null);
  const widgetIdRef = useRef(null);

  const onTokenReceivedRef = useRef(onTokenReceived);
  onTokenReceivedRef.current = onTokenReceived;

  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;

  const invisibleRef = useRef(invisible);
  invisibleRef.current = invisible;

  // Expose reset method to parent components
  useImperativeHandle(ref, () => ({
    reset: () => {
      if (widgetIdRef.current !== null && window.turnstile) {
        logger.log('Turnstile: Resetting widget with ID:', widgetIdRef.current);
        window.turnstile.reset(widgetIdRef.current);
      } else {
        logger.log('Turnstile: Cannot reset - widget not initialized');
      }
    },
  }));

  useEffect(() => {
    // Auto-pass on localhost for Playwright/automated testing
    if (typeof window !== 'undefined' && window.location.hostname === 'localhost') {
      logger.log('Turnstile: Localhost detected, auto-passing with dummy token');
      setTimeout(() => {
        if (onTokenReceivedRef.current) {
          onTokenReceivedRef.current('LOCALHOST_BYPASS_TOKEN');
        }
      }, 100);
      return () => {}; // Skip normal Turnstile loading
    }

    let script = document.getElementById(SCRIPT_ID);

    const renderWidget = () => {
      logger.log('Turnstile: Attempting to render widget...');
      if (turnstileRef.current && window.turnstile) {
        // Save the currently focused element before Turnstile renders its iframe.
        // The iframe can steal focus even in interaction-only mode, causing
        // input fields (e.g. email) to flash/deselect while the user is typing.
        const previouslyFocused = document.activeElement;

        const widgetId = window.turnstile.render(turnstileRef.current, {
          sitekey: TURNSTILE_SITE_KEY,
          ...(invisibleRef.current ? { appearance: 'interaction-only' } : {}),
          callback: (token) => {
            logger.log('Turnstile: Token received.');
            if (onTokenReceivedRef.current) {
              onTokenReceivedRef.current(token);
            }
          },
          'error-callback': (errorCode) => {
            logger.warn('Turnstile: Error occurred, code:', errorCode);
            if (onErrorRef.current) {
              onErrorRef.current(errorCode);
            }
          },
          'expired-callback': () => {
            logger.log('Turnstile: Token expired, resetting widget.');
            if (widgetIdRef.current !== null && window.turnstile) {
              window.turnstile.reset(widgetIdRef.current);
            }
          },
          theme: 'dark',
        });
        widgetIdRef.current = widgetId;
        logger.log('Turnstile: Widget rendered with ID:', widgetId);

        // Restore focus after Turnstile iframe settles.
        // The iframe can steal focus both synchronously and asynchronously,
        // so we check at two points: next frame and after a short delay.
        if (previouslyFocused && previouslyFocused !== document.body) {
          const restoreFocus = () => {
            if (document.activeElement !== previouslyFocused && document.body.contains(previouslyFocused)) {
              previouslyFocused.focus();
            }
          };
          requestAnimationFrame(restoreFocus);
          setTimeout(restoreFocus, 300);
        }
      } else {
        logger.log('Turnstile: Widget render failed - ref or window.turnstile not ready.');
      }
    };

    // Callback function once the script is loaded
    window.onTurnstileLoad = () => {
      logger.log('Turnstile: Cloudflare script loaded.');
      renderWidget();
    };

    if (!script) {
      logger.log('Turnstile: Script not found, creating and appending...');
      script = document.createElement('script');
      script.id = SCRIPT_ID;
      script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?onload=onTurnstileLoad';
      script.async = true;
      script.defer = true;
      document.body.appendChild(script);
    } else if (window.turnstile) {
      // If script exists and is loaded, render immediately
      logger.log('Turnstile: Script already exists, rendering widget directly.');
      renderWidget();
    }

    return () => {
      logger.log('Turnstile: Cleaning up widget.');
      if (widgetIdRef.current && window.turnstile) {
        window.turnstile.remove(widgetIdRef.current);
      }
    };
  }, []);

  // On localhost, Turnstile is bypassed — hide the empty container that gets styled by CF
  if (typeof window !== 'undefined' && window.location.hostname === 'localhost') {
    return null;
  }

  return <View ref={turnstileRef} />;
});

export default TurnstileWidget;
